import type { FindingView } from "../../api/types.js";
import { displayRuleId } from "../../app/rule-id.js";

export type CompositionClaim = "recorded" | "correlated";
export type CompositionStage = "entry" | "capability" | "sink" | "impact";
export type CompositionTone = "entry" | "hop" | "sink" | "sibling" | "impact";

export interface CompositionNode {
  readonly id: string;
  readonly stage: CompositionStage;
  readonly label: string;
  readonly detail: string;
  readonly brutal: string;
  readonly path: string | null;
  readonly ruleId: string | null;
  readonly cwe: string | null;
  readonly snippet: string | null;
  readonly caption: string;
  readonly tone: CompositionTone;
  readonly role: string;
  readonly proof: string;
  readonly story: string;
  readonly findingId: string | null;
  readonly openFindingId: string;
  readonly lineStart: number | null;
  readonly hopNumber: number | null;
  readonly focus: boolean;
  readonly replay: FindingView["evidence"]["replayStatus"] | null;
  readonly severity: FindingView["severity"] | null;
}

export interface CompositionEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly claim: CompositionClaim;
  readonly flow: "spine" | "in" | "out";
  readonly why: string;
}

export interface CompositionGraph {
  readonly nodes: readonly CompositionNode[];
  readonly edges: readonly CompositionEdge[];
  readonly breakFindingId: string;
  readonly breakSentence: string;
  readonly honesty: string;
  readonly relatedCount: number;
  readonly recordedHops: number;
  readonly spineIds: readonly string[];
}

const MAX_RELATED = 6;
const BOX_NAME = 22;

const STAGE_LABEL: Record<CompositionStage, string> = {
  entry: "Start / entry evidence",
  capability: "Recorded hops",
  sink: "Security-sensitive operation",
  impact: "Potential impact",
};

function compact(value: string, fallback: string, max = 72): string {
  const normalized = value.replace(/\s+/gu, " ").trim();
  if (!normalized) return fallback;
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1).trimEnd()}…`;
}

function fileName(path: string): string {
  return path.split("/").at(-1) ?? path;
}

/** Pick the token that fits a hop disc: method(), file, or last class segment. */
export function displayHopName(step: string): string {
  const raw = step.replace(/\(\)\s*$/u, "").trim();
  if (!raw) return "Hop";
  if (raw.includes("/") || /\.\w{1,8}$/u.test(raw)) {
    return compact(fileName(raw), raw, BOX_NAME);
  }
  const method = raw.match(/([A-Za-z_]\w+)$/u);
  const methodName = method?.[1];
  if (methodName && raw.includes(".")) return compact(`${methodName}()`, methodName, BOX_NAME);
  return compact(raw, raw, BOX_NAME);
}

export function displayCitedCall(snippet: string, path: string, line: number): string {
  const cleaned = snippet.replace(/\s+/gu, " ").trim();
  const calls = [...cleaned.matchAll(/([A-Za-z_][\w$]*)\s*\(/gu)];
  const last = calls.at(-1);
  const callName = last?.[1];
  if (callName) return compact(`${callName}()`, callName, BOX_NAME);
  return compact(`${fileName(path)}:${line}`, `${fileName(path)}:${line}`, BOX_NAME);
}

function shortStory(lead: string, name: string): string {
  return compact(`${lead} ${name}`, lead, 42);
}

function siblingStage(finding: FindingView): CompositionStage {
  const blob =
    `${finding.analysisFamily ?? ""} ${finding.ruleId} ${finding.cwe} ${finding.title}`.toLocaleLowerCase();
  if (
    finding.analysisFamily === "secrets" ||
    finding.analysisFamily === "iac" ||
    finding.analysisFamily === "container" ||
    /cwe-798|secret|credential|cwe-862|cwe-863|fail.open/u.test(blob)
  ) {
    return "capability";
  }
  return "sink";
}

export function faultShort(finding: FindingView): string {
  const blob =
    `${finding.analysisFamily ?? ""} ${finding.ruleId} ${finding.cwe} ${finding.title} ${finding.evidence.redactedSnippet ?? ""}`.toLocaleLowerCase();
  if (/readobject|objectinputstream|deserial|cwe-502/u.test(blob)) return "Deser / RCE";
  if (/executequery|jdbc|cwe-89|\bsql\b/u.test(blob)) return "SQLi";
  if (/innerhtml|xss|cwe-79/u.test(blob)) return "XSS";
  if (/eval|new function|cwe-94|cwe-95/u.test(blob)) return "Code inj";
  if (/cwe-798|secret|credential|password/u.test(blob) || finding.analysisFamily === "secrets")
    return "Secret";
  if (finding.analysisFamily === "iac" || finding.analysisFamily === "container") return "IaC";
  if (/fail(?:s|ed)?[\s-]?open|cwe-755|cwe-862|cwe-863/u.test(blob)) return "Fail-open";
  return "Sink";
}

function sinkCall(finding: FindingView): string {
  const cited = (finding.evidence.redactedSnippet ?? "").replace(/\s+/gu, " ").trim();
  if (/\breturn\s+true\b/iu.test(cited)) return "return true";
  if (/\breturn\s+false\b/iu.test(cited)) return "return false";
  return displayCitedCall(cited, finding.location.path, finding.location.lineStart);
}

function sinkNarration(finding: FindingView, sinkLabel: string, loc: string): string {
  const cited = (finding.evidence.redactedSnippet ?? "").replace(/\s+/gu, " ").trim();
  const blob =
    `${finding.title} ${finding.description} ${finding.ruleId} ${finding.cwe ?? ""} ${cited}`.toLocaleLowerCase();
  const replay =
    finding.evidence.replayStatus === "matched"
      ? "Replay confirms the cited bytes, not a live exploit."
      : `Replay is ${finding.evidence.replayStatus}, so execution still needs verification.`;
  if (/fail(?:s|ed)?[\s-]?open|cwe-755|cwe-862|cwe-863|return true/u.test(blob)) {
    return `At ${loc}, ${sinkLabel} converts the error path into an allow decision. An attacker who can make authorization fail may be treated as authorized. ${replay}`;
  }
  if (/eval|new function|cwe-94|cwe-95/u.test(blob)) {
    return `At ${loc}, ${sinkLabel} interprets data as executable code. Attacker-controlled text reaching this call can become runtime instructions. ${replay}`;
  }
  if (/executequery|executeupdate|jdbc|cwe-89|\bsql\b/u.test(blob)) {
    return `At ${loc}, ${sinkLabel} sends data to the SQL interpreter. Unbound attacker input can change the query's meaning. ${replay}`;
  }
  if (/innerhtml|document\.write|cwe-79|\bxss\b/u.test(blob)) {
    return `At ${loc}, ${sinkLabel} places data into an executable browser context. Unsanitized attacker input can run as script. ${replay}`;
  }
  return `At ${loc}, ${sinkLabel} is the security-sensitive operation named by ${displayRuleId(finding.ruleId)}. Attacker-controlled data reaching it can produce ${faultShort(finding)}. ${replay}`;
}

function findingBrutal(finding: FindingView): string {
  const cited = (finding.evidence.redactedSnippet ?? "").replace(/\s+/gu, " ").trim();
  const loc = `${fileName(finding.location.path)}:${finding.location.lineStart}`;
  const blob =
    `${cited} ${finding.title} ${finding.description} ${finding.ruleId}`.toLocaleLowerCase();
  if (/executequery|executeupdate|createstatement|jdbc|\bsql\b/u.test(blob)) {
    return `${loc} calls \`${cited || "a JDBC Statement"}\`. That is an SQL interpreter sink. If the string is concatenated, the database will obey attacker syntax. Replay matches bytes, not a live query.`;
  }
  if (/readobject|objectinputstream|deserial/u.test(blob)) {
    return `${loc} is a Java deserialization sink (\`${cited}\`). readObject instantiates whatever type the stream names. We have not executed a gadget chain.`;
  }
  if (/eval|new function|innerhtml|document\.write/u.test(blob)) {
    return `${loc} is an eval/markup sink (\`${cited}\`). Attacker text can become executable syntax. This excerpt does not prove a browser ran it.`;
  }
  return `${loc}: ${finding.title}. Replay is ${finding.evidence.replayStatus}. Treat this as a sealed defect until a named 4V decision says otherwise.`;
}

function sharesEntry(a: FindingView, b: FindingView): boolean {
  const left = a.reachability.entrypoint?.trim();
  const right = b.reachability.entrypoint?.trim();
  return Boolean(left && right && left === right);
}

/**
 * Scan composition map. Recorded hops come only from this finding's sealed
 * reachability path. Sibling findings attach as same-scan correlations.
 * This is not a live exploit and must not be projected as a confirmed chain.
 */
export function buildFindingCompositionGraph(
  focus: FindingView,
  related: readonly FindingView[] = [],
): CompositionGraph {
  const loc = `${fileName(focus.location.path)}:${focus.location.lineStart}`;
  const entryName = focus.reachability.entrypoint?.trim() || null;
  const hops = focus.reachability.executionPath
    .map((step) => step.replace(/\(\)\s*$/u, "").trim())
    .filter(
      (step) =>
        step.length > 0 &&
        step !== entryName &&
        !/tree-sitter|codex-security|validator|engine/iu.test(step),
    );
  const entryLabel = displayHopName(entryName ?? "Start not established");
  const sinkLabel = sinkCall(focus);
  const impactLabel = faultShort(focus);
  const uniqueHops = [...new Set(hops)]
    .filter((hop) => displayHopName(hop) !== sinkLabel && displayHopName(hop) !== entryLabel)
    .slice(0, 4);

  const nodes: CompositionNode[] = [
    {
      id: "entry",
      stage: "entry",
      label: entryLabel,
      detail: entryName
        ? `Recorded entry ${entryName} on this finding's sealed path.`
        : "The scan did not establish an externally reachable starting point. This is an evidence gap, not a safe result and not a proven attacker route.",
      brutal: entryName
        ? `${entryName} is on the sealed execution path. That is how the detector says this sink is reached. It is not proof of a live session.`
        : "A starting point was not proven. Validate routes, message consumers, jobs, and trust-boundary inputs before claiming exploitability.",
      path: null,
      ruleId: focus.ruleId,
      cwe: focus.cwe ?? null,
      snippet: null,
      caption: "Entry",
      tone: "entry",
      role: "Sealed entry",
      proof: entryName
        ? `Execution path on this finding names ${entryName}.`
        : "No externally reachable start is recorded for this finding.",
      story: entryName ? shortStory("Starts at", entryLabel) : "No recorded start",
      findingId: null,
      openFindingId: focus.id,
      lineStart: null,
      hopNumber: 1,
      focus: false,
      replay: null,
      severity: null,
    },
  ];
  uniqueHops.forEach((hop, index) => {
    const name = displayHopName(hop);
    nodes.push({
      id: `hop-${hop}`,
      stage: "capability",
      label: name,
      caption: "Hop",
      tone: "hop",
      role: "Recorded hop",
      proof: `Sealed execution path includes ${hop}.`,
      story: shortStory("Then", name),
      detail: `Hop recorded on this finding's execution path: ${hop}.`,
      brutal: `${hop} sits between entry and the cited sink on the sealed path. It is a recorded hop, not a second finding.`,
      path: hop.includes("/") ? hop : null,
      ruleId: focus.ruleId,
      cwe: focus.cwe ?? null,
      snippet: null,
      findingId: null,
      openFindingId: focus.id,
      lineStart: null,
      hopNumber: index + 2,
      focus: false,
      replay: null,
      severity: null,
    });
  });
  const sinkHop = uniqueHops.length + 2;
  nodes.push({
    id: `finding-${focus.id}`,
    stage: "sink",
    label: sinkLabel,
    caption: impactLabel,
    tone: "sink",
    role: "This sink",
    proof: `Replay ${focus.evidence.replayStatus} at ${loc}. Rule ${displayRuleId(focus.ruleId)}.`,
    story: shortStory("Cited sink", sinkLabel),
    detail: sinkNarration(focus, sinkLabel, loc),
    brutal: findingBrutal(focus),
    path: focus.location.path,
    ruleId: focus.ruleId,
    cwe: focus.cwe ?? null,
    snippet: (focus.evidence.redactedSnippet ?? "").replace(/\s+/gu, " ").trim() || null,
    findingId: focus.id,
    openFindingId: focus.id,
    lineStart: focus.location.lineStart,
    hopNumber: sinkHop,
    focus: true,
    replay: focus.evidence.replayStatus,
    severity: focus.severity,
  });
  nodes.push({
    id: "impact",
    stage: "impact",
    label: impactLabel,
    caption: "Impact",
    tone: "impact",
    role: "Claimed impact",
    proof: compact(focus.impact, "Impact is not recorded on this finding.", 160),
    story: shortStory("Claimed", impactLabel),
    detail: compact(focus.impact, "Impact is not yet recorded.", 88),
    brutal: compact(
      focus.impact,
      "Impact is not recorded. Do not brief a breach you cannot cite.",
      220,
    ),
    path: focus.location.path,
    ruleId: focus.ruleId,
    cwe: focus.cwe ?? null,
    snippet: null,
    findingId: null,
    openFindingId: focus.id,
    lineStart: focus.location.lineStart,
    hopNumber: sinkHop + 1,
    focus: false,
    replay: null,
    severity: null,
  });

  const edges: CompositionEdge[] = [];
  const spine = [
    "entry",
    ...uniqueHops.map((hop) => `hop-${hop}`),
    `finding-${focus.id}`,
    "impact",
  ];
  for (let index = 0; index < spine.length - 1; index += 1) {
    const from = spine[index] as string;
    const to = spine[index + 1] as string;
    const recorded = to !== "impact";
    edges.push({
      id: `${from}->${to}`,
      from,
      to,
      claim: recorded ? "recorded" : "correlated",
      flow: recorded ? "spine" : "out",
      why: recorded
        ? "Sealed reachability on this finding."
        : "Impact is the detector's recorded consequence, not a proven breach.",
    });
  }

  const sameSnapshot = related.filter(
    (item) => item.id !== focus.id && item.snapshotId === focus.snapshotId,
  );
  const ranked = [
    ...sameSnapshot.filter((item) => item.location.path === focus.location.path),
    ...sameSnapshot.filter(
      (item) => item.location.path !== focus.location.path && sharesEntry(item, focus),
    ),
    ...sameSnapshot.filter(
      (item) => item.location.path !== focus.location.path && !sharesEntry(item, focus),
    ),
  ].slice(0, MAX_RELATED);

  for (const sibling of ranked) {
    const id = `finding-${sibling.id}`;
    const stage = siblingStage(sibling);
    nodes.push({
      id,
      stage,
      label: compact(
        `${faultShort(sibling)}:${sibling.location.lineStart}`,
        faultShort(sibling),
        BOX_NAME,
      ),
      caption: "Same scan",
      tone: "sibling",
      role: "Same-scan finding",
      proof: `Replay ${sibling.evidence.replayStatus}. Rule ${displayRuleId(sibling.ruleId)}. Not on this finding's sealed path.`,
      story: shortStory("Same scan", faultShort(sibling)),
      detail: `${fileName(sibling.location.path)}:${sibling.location.lineStart} · ${displayRuleId(sibling.ruleId)}`,
      brutal: findingBrutal(sibling),
      path: sibling.location.path,
      ruleId: sibling.ruleId,
      cwe: sibling.cwe ?? null,
      snippet: (sibling.evidence.redactedSnippet ?? "").replace(/\s+/gu, " ").trim() || null,
      findingId: sibling.id,
      openFindingId: sibling.id,
      lineStart: sibling.location.lineStart,
      hopNumber: null,
      focus: false,
      replay: sibling.evidence.replayStatus,
      severity: sibling.severity,
    });
    if (sibling.location.path === focus.location.path) {
      edges.push({
        id: `${id}->finding-${focus.id}`,
        from: id,
        to: `finding-${focus.id}`,
        claim: "correlated",
        flow: "in",
        why: "Same frozen file on this snapshot. Adjacency is not data-flow proof.",
      });
    } else if (sharesEntry(sibling, focus)) {
      edges.push({
        id: `entry->${id}`,
        from: "entry",
        to: id,
        claim: "correlated",
        flow: "in",
        why: "Same recorded entrypoint on this snapshot. Shared entry is not a chained exploit.",
      });
    } else {
      edges.push({
        id: `${id}->impact`,
        from: id,
        to: "impact",
        claim: "correlated",
        flow: "out",
        why: "Same snapshot composition. No sealed data-flow between these files is recorded.",
      });
    }
  }

  const rem = focus.remediation[0]?.replace(/\s+/gu, " ").trim();
  return {
    nodes,
    edges,
    breakFindingId: focus.id,
    breakSentence: rem
      ? `Break the path at ${loc}: ${rem} Then re-scan this snapshot's fixture. Nothing here writes the repository.`
      : `Break the path at ${loc} so the cited sink cannot execute. Then re-scan this snapshot's fixture.`,
    honesty:
      "A security-sensitive operation (often called a sink) is where untrusted data could cause harm. Solid hops are recorded on this finding; dashed links are same-scan correlations. This is not a live exploit. If the start is not established, this is an incomplete exposure path—not a confirmed attack chain.",
    relatedCount: ranked.length,
    recordedHops: uniqueHops.length + (entryName ? 1 : 0),
    spineIds: spine,
  };
}

const CLOUD_W = 152;
const CLOUD_H = 58;

function boxesOverlap(
  a: { x: number; y: number },
  b: { x: number; y: number },
  padX: number,
  padY: number,
): boolean {
  return Math.abs(a.x - b.x) < padX && Math.abs(a.y - b.y) < padY;
}

function placeStoryClouds(
  spineIds: readonly string[],
  placed: Readonly<Record<string, { x: number; y: number }>>,
  width: number,
): Record<string, { x: number; y: number }> {
  const clouds: Record<string, { x: number; y: number }> = {};
  const taken: { x: number; y: number }[] = [];
  const blocked = Object.values(placed);

  for (const id of spineIds) {
    const point = placed[id];
    if (!point) continue;
    const tries = [
      { x: point.x, y: point.y - 118 },
      { x: point.x - 72, y: point.y - 118 },
      { x: point.x + 72, y: point.y - 118 },
      { x: point.x, y: point.y - 178 },
      { x: point.x - 90, y: point.y + 96 },
      { x: point.x + 90, y: point.y + 96 },
      { x: point.x, y: point.y + 108 },
    ];
    const chosen = tries.find((candidate) => {
      if (candidate.x < 86 || candidate.x > width - 86 || candidate.y < 36) return false;
      const hitsCloud = taken.some((other) =>
        boxesOverlap(candidate, other, CLOUD_W * 0.72, CLOUD_H * 0.78),
      );
      const hitsNode = blocked.some((node) => boxesOverlap(candidate, node, 78, 52));
      return !hitsCloud && !hitsNode;
    }) ?? { x: Math.min(width - 90, Math.max(90, point.x)), y: 48 };
    clouds[id] = chosen;
    taken.push(chosen);
  }
  return clouds;
}

export function compositionLayout(graph: CompositionGraph): {
  width: number;
  height: number;
  placed: Readonly<Record<string, { x: number; y: number; node: CompositionNode }>>;
  clouds: Readonly<Record<string, { x: number; y: number }>>;
  zones: readonly {
    id: CompositionStage;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
} {
  const columns: Record<CompositionStage, CompositionNode[]> = {
    entry: [],
    capability: [],
    sink: [],
    impact: [],
  };
  for (const node of graph.nodes) columns[node.stage].push(node);
  const xs: Record<CompositionStage, number> = {
    entry: 160,
    capability: 480,
    sink: 820,
    impact: 1140,
  };
  const siblingCounts = Object.values(columns).map(
    (column) => column.filter((node) => node.hopNumber === null).length,
  );
  const hopCounts = Object.values(columns).map(
    (column) => column.filter((node) => node.hopNumber !== null).length,
  );
  const width = 1280;
  const hopRow = 92;
  const siblingRow = 70;
  const spineY = 248;
  const extraHops = Math.max(0, ...hopCounts.map((count) => count - 1));
  const extraSiblings = Math.max(0, ...siblingCounts);
  const height = Math.max(460, spineY + extraHops * hopRow + extraSiblings * siblingRow + 88);
  const placed: Record<string, { x: number; y: number; node: CompositionNode }> = {};
  for (const stage of ["entry", "capability", "sink", "impact"] as const) {
    const column = columns[stage];
    const hops = column.filter((node) => node.hopNumber !== null);
    const siblings = column.filter((node) => node.hopNumber === null);
    hops.forEach((node, index) => {
      placed[node.id] = {
        x: xs[stage],
        y: spineY + (index - (hops.length - 1) / 2) * hopRow,
        node,
      };
    });
    siblings.forEach((node, index) => {
      placed[node.id] = {
        x: xs[stage] - 28,
        y: spineY + 86 + index * siblingRow,
        node,
      };
    });
  }
  const zones = (["entry", "capability", "sink", "impact"] as const).map((stage) => ({
    id: stage,
    label: STAGE_LABEL[stage],
    x: xs[stage] - 140,
    y: 16,
    width: 280,
    height: height - 32,
  }));
  return {
    width,
    height,
    placed,
    clouds: placeStoryClouds(graph.spineIds, placed, width),
    zones,
  };
}
